import { useEffect, useState } from "react";
import { base44 } from "@/api/base44Client";
import { Image } from "@/components/ui/image";
import ListingGallery from "@/components/yts/ListingGallery";
import ViatorBadge from "@/components/yts/ViatorBadge";
import ShareButton from "@/components/yts/ShareButton";
import ViatorEditModal from "@/components/yts/ViatorEditModal";
import { PartnerLockup } from "@/components/yts/PartnerMarks";
import { priceFromLabel, ratingLabel, viatorTitleLabel, viatorDurationLabel, withViatorTracking } from "@/lib/viator";
import { categoryLabel, neighborhoodLabel } from "@/lib/locales";
import { useLang } from "@/lib/i18n";
import {
  Check, ChevronLeft, ChevronRight, Clock, ExternalLink, MapPin, Pencil, ShieldCheck, Star, X } from
"lucide-react";

const monoHead = "font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-[#55534e]";
const moduleBox = "rounded-[15px] border border-[#e4e2dc] bg-gradient-to-b from-white via-white to-[#faf9f6] shadow-[inset_0_1px_0_rgba(255,255,255,0.98),0_3px_12px_rgba(17,17,17,0.035)] dark:border-white/[0.10] dark:bg-gradient-to-b dark:from-[#242520] dark:to-[#1b1c19] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.045),0_3px_12px_rgba(0,0,0,0.16)]";

function InfoLine({ label, children }) {
  return (
    <div className="flex items-baseline gap-4 py-2.5 border-b border-[#e4e2dc] last:border-b-0 dark:border-white/[0.10]">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-[#6d6d6a] w-[92px] shrink-0 pt-[2px]">
        {label}
      </div>
      <div className="text-[14px] font-medium text-[#171717] flex-1 min-w-0">{children}</div>
    </div>);

}

/**
 * Bookable experience detail, rendered as its own page of the site: the header,
 * the search and the bottom nav stay around it. Everything else lives in the app
 * — only the final payment step hands off to Viator.
 */
export default function ViatorDetailContent({ id, overlay = false, initialRecord = null }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(false);
  const isAdmin = me?.role === "admin";
  const { t, lang } = useLang();

  useEffect(() => {
    let active = true;
    if (initialRecord) {
      setRecord(initialRecord);
      setLoading(false);
    } else {
      setLoading(true);
      (async () => {
        try {
          const fetched = await base44.entities.ViatorExperience.get(id);
          if (active) setRecord(fetched || null);
        } catch {
          if (active) setRecord(null);
        } finally {
          if (active) setLoading(false);
        }
      })();
    }
    base44.auth.me().then((user) => { if (active) setMe(user); }).catch(() => {});
    return () => { active = false; };
  }, [id, initialRecord]);

  if (loading)
  return (
    <div className="grid place-items-center py-24">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
      </div>);


  if (!record)
  return (
    <div className="grid place-items-center text-center px-6 py-24">
        <div>
          <h1 className="text-[22px] font-black">{t("detail.experienceNotFound")}</h1>
          <p className="text-[14px] text-[#6d6d6a] mt-1">{t("detail.experienceGone")}</p>
        </div>
      </div>);


  const gallery = (record.images || []).filter(Boolean);
  const title = viatorTitleLabel(record,lang);
  const destination = neighborhoodLabel(record.destination || "Puerto Rico", lang);
  const category = categoryLabel(record.category || (lang === "es" ? "experiencia" : "experience"), lang);
  const generatedDescription = lang === "es"
    ? `Explora ${title} en ${destination}. Esta experiencia de ${category.toLowerCase()} incluye los detalles disponibles de duración, precio, punto de encuentro y reserva. Revisa la información antes de reservar.`
    : `Explore ${title} in ${destination}. This ${category.toLowerCase()} listing brings together the available duration, price, meeting-point and booking details. Review the information before booking.`;
  const description = lang === "es"
    ? (record.description_es || (record.description ? t("detail.translationPending") : generatedDescription))
    : (record.description || generatedDescription);
  const included = lang === "es" ? (record.included_es || []) : (record.included || []);
  const meetingPoint = lang === "es" ? record.meeting_point_es : record.meeting_point;
  const cancellationText = lang === "es" ? record.cancellation_text_es : record.cancellation_text;
  const duration = viatorDurationLabel(record.duration_text,lang);
  const price = priceFromLabel(record.price_from,lang);
  const rating = ratingLabel(record.rating);
  const book = withViatorTracking(record.booking_url);
  // The booking bar sits on the card's own floor; on the standalone page it
  // clears the bottom nav.
  const barBottom = overlay ?
  "bottom-0" :
  "bottom-[calc(68px_+_env(safe-area-inset-bottom,0px))] sm:bottom-0";

  return (
    <div className="max-w-[920px] mx-auto px-4 sm:px-6 pt-5 pb-10">
      <ListingGallery images={gallery} alt={title} onOpen={setLightbox} />

      {/* Same title block as a local business card: the line above the name, the
            name itself, the partner badge, then edit for an editor. */}
      <div className="flex items-start justify-between gap-3 mt-5">
        <div className="min-w-0">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase font-bold text-[#6d6d6a]">
            {[categoryLabel(record.category,lang), neighborhoodLabel(record.destination,lang)].filter(Boolean).join(" · ")}
          </div>
          <h1 className="text-[clamp(28px,5vw,40px)] font-black tracking-[-0.04em] leading-[1.02] mt-1.5">
            {title}
          </h1>
          <ViatorBadge className="mt-2" />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2 text-[13px] font-bold text-[#171717]">
            {price && <span className="font-mono tracking-wider">{price} {t("detail.perPerson")}</span>}
            {rating &&
            <span className="inline-flex items-center gap-1.5 text-[#6d6d6a]">
                <Star className="w-4 h-4 fill-[#FF0000] text-[#FF0000]" />
                {rating}
                {record.review_count ? ` (${record.review_count.toLocaleString(lang === "es" ? "es-PR" : "en-US")} ${t("detail.reviews")})` : ""}
              </span>
            }
            {record.duration_text &&
            <span className="inline-flex items-center gap-1.5 text-[#6d6d6a]">
                <Clock className="w-4 h-4" /> {duration}
              </span>
            }
          </div>
        </div>
        {isAdmin &&
        <button
          onClick={() => setEditing(true)}
          className="w-12 h-12 shrink-0 border border-[#171717] bg-white grid place-items-center hover:bg-[#171717] hover:text-white transition-colors"
          aria-label={lang === "es" ? "Editar experiencia" : "Edit experience"}>
          
            <Pencil className="w-5 h-5" />
          </button>
        }
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <ShareButton title={title} url={`${window.location.origin}/experience/${record.id}`} />
      </div>

      

      

      {description &&
      <div className="mt-6">
          <div className={monoHead + " mb-2"}>{t("detail.aboutExperience")}</div>
          <p className="text-[15.5px] leading-[1.6] text-[#171717] whitespace-pre-line">{description}</p>
        </div>
      }

      {included.length > 0 &&
      <div className="mt-6">
          <div className={monoHead + " mb-2"}>{t("detail.included")}</div>
          <div className={moduleBox + " px-4 py-3"}>
            <ul className="space-y-2">
              {included.map((item, i) =>
            <li key={i} className="flex items-start gap-2 text-[14px] text-[#171717]">
                  <Check className="w-4 h-4 mt-[3px] shrink-0 text-[#F97316]" /> {item}
                </li>
            )}
            </ul>
          </div>
        </div>
      }

      <div className="mt-6">
        <div className={monoHead + " mb-2"}>{t("detail.goodToKnow")}</div>
        <div className={moduleBox + " px-4"}>
          {record.duration_text && <InfoLine label={t("detail.duration")}>{duration}</InfoLine>}
          {price && <InfoLine label={t("detail.from")}>{price} {t("detail.perPerson")}</InfoLine>}
          {meetingPoint &&
          <InfoLine label={t("detail.meeting")}>
              <span className="inline-flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 mt-[3px] shrink-0 text-[#6d6d6a]" /> {meetingPoint}
              </span>
            </InfoLine>
          }
          {cancellationText && <InfoLine label={t("detail.cancellation")}>{cancellationText}</InfoLine>}
          <InfoLine label={t("detail.availability")}>{t("detail.availabilityText")}</InfoLine>
          <InfoLine label={t("detail.bookedVia")}>
            <span className="inline-flex items-center gap-2.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[#F97316]" />
              <PartnerLockup />
            </span>
          </InfoLine>
        </div>
      </div>

      {lightbox != null &&
      <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center" onClick={() => setLightbox(null)}>
          {gallery.length > 1 &&
        <div className="absolute top-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/15 text-white text-[12px] font-bold">
              {lightbox + 1} / {gallery.length}
            </div>
        }
          <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox(null); }} className="absolute top-4 right-4 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" aria-label={lang === "es" ? "Cerrar foto" : "Close photo viewer"}>
            <X className="w-5 h-5" />
          </button>
          <button
          onClick={(e) => {
            e.stopPropagation();
            setLightbox((i) => (i - 1 + gallery.length) % gallery.length);
          }}
          type="button"
          className="absolute left-3 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          aria-label={lang === "es" ? "Foto anterior" : "Previous photo"}>
          
            <ChevronLeft className="w-6 h-6" />
          </button>
          <Image src={gallery[lightbox]} alt={`${title} photo ${lightbox + 1}`} fittingType="fit" className="max-h-[82dvh] max-w-[92vw] object-contain" />
          <button
          onClick={(e) => {
            e.stopPropagation();
            setLightbox((i) => (i + 1) % gallery.length);
          }}
          type="button"
          className="absolute right-3 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          aria-label={lang === "es" ? "Foto siguiente" : "Next photo"}>
          
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      }

      {book &&
      <div className={`sticky z-10 mt-6 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2.5 bg-white/95 backdrop-blur-xl border-t border-black/[0.08] shadow-[0_-8px_24px_rgba(0,0,0,0.07)] dark:border-white/[0.10] dark:bg-[#171816]/95 ${barBottom}`}>
          <div className="max-w-[920px] mx-auto flex items-center gap-3">
            <div className="min-w-0 flex-1">
              {price && <div className="text-[13px] font-extrabold text-[#171717] font-mono tracking-wider">{price}</div>}
              <div className="text-[11px] text-[#6d6d6a]">{t("detail.handledBy")}</div>
            </div>
            <a
            href={book}
            target="_blank"
            rel="noopener"
            onClick={() => {
              // Counts the hand-off to Viator, so an empty affiliate report can
              // be traced to either no clicks or an attribution problem.
              base44.analytics.track({
                eventName: "viator_booking_click",
                properties: {
                  product_code: record.product_code || "",
                  destination: record.destination || "",
                },
              });
              // Logs who is worth pushing: which experiences get tapped, and from which page.
              base44.functions
                .invoke("viator-click-log", {
                  product_code: record.product_code || "",
                  title,
                  page_path: `${window.location.pathname}${window.location.search}`,
                })
                .catch(() => {});
            }}
            className="relative shrink-0 overflow-hidden rounded-[12px] border border-[#D90000] bg-gradient-to-b from-[#FF5C5C] via-[#EF0000] to-[#D90000] px-5 py-3 text-[12px] font-extrabold !text-white [text-shadow:0_1px_1px_rgba(80,0,0,0.35)] shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_3px_10px_rgba(239,0,0,0.2)] inline-flex items-center gap-1.5 transition hover:from-[#FF7777] hover:via-[#FF3333] hover:to-[#D90000] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#171717] focus-visible:ring-offset-2">
            
              <ExternalLink className="w-4 h-4" /> {t("detail.bookViator")}
            </a>
          </div>
        </div>
      }

      {editing &&
      <ViatorEditModal
        experience={record}
        onClose={() => setEditing(false)}
        onSaved={setRecord} />

      }
    </div>);

}